import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnBadge } from 'src/app/components/widgets/data-table/columns/data-table-column-badge/data-table-column-badge.config';
import { DataTableColumnActions } from 'src/app/components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import { Regulator, Vlt, regulatorStatusBadgeClass, vltStatusLabel } from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { withDeleteFeedback } from '../regulators-ui.utils';

@Component({
  selector: 'app-regulator-vlt-index-page',
  imports: [BaseIndexPageComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-vlt-index-page.component.html',
  styleUrl: './regulator-vlt-index-page.component.scss',
})
export class RegulatorVltIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;
  contextItems: RegulatorContextItem[] = [];

  readonly regulatorId: number;

  /** Adapter used by the data-table delete button (nested endpoint + toasts). */
  readonly deleteService = {
    delete: (vltId: number) =>
      withDeleteFeedback(this.regulatorsService.deleteVlt(this.regulatorId, vltId), this.alertNotificationService, 'VLT')
  };

  private subscriptions = new Subscription();

  constructor(
    protected regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));

    this.baseIndexPageConfig = {
      pageTitle: 'Regulators',
      cardTitle: 'VLT',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          code: null,
          serialNumber: null,
          venue: null,
          city: null,
          status: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            value: (item: Vlt) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'serialNumber',
            header: 'Serial Number',
            filter: 'text',
            value: (item: Vlt) => item?.serialNumber ?? ''
          }),
          new DataTableColumnText({
            attribute: 'venue',
            header: 'Venue',
            filter: 'text',
            value: (item: Vlt) => item?.venue ?? ''
          }),
          new DataTableColumnText({
            attribute: 'city',
            header: 'City',
            filter: 'text',
            value: (item: Vlt) => item?.city ?? ''
          }),
          new DataTableColumnBadge({
            attribute: 'status',
            header: 'Status',
            filter: 'select',
            filterOptionsSource: 'vltStatuses',
            value: (item: Vlt) => item?.status ? vltStatusLabel(item.status) : null,
            badgeClass: (item: Vlt) => regulatorStatusBadgeClass(item?.status)
          }),
          new DataTableColumnText({
            attribute: 'lastSeenTime',
            header: 'Last Seen',
            value: (item: Vlt) => this.commonService.formatDateValue(item?.lastSeenTime, true)
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            sortable: false,
            cellClass: 'text-center',
            style: { width: '150px' },
            buttons: [
              {
                icon: 'fa-eye',
                tooltip: 'View VLT dashboard',
                aclAction: 'view',
                onClick: (item: Vlt) => this.routeService.regulatorVltDashboard(this.regulatorId, item.vltId)
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit VLT',
                aclAction: 'modify',
                onClick: (item: Vlt) => this.routeService.regulatorVltUpdate(this.regulatorId, item.vltId)
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete VLT',
                aclAction: 'delete',
                isDelete: true
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.regulatorsService.listVlts(this.regulatorId, options),
      responseListModelName: 'vlts',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        const canCreate = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
        this.baseIndexPageConfig.addNewItemButton = canCreate
          ? { type: 'success', title: 'Create VLT', onClick: () => this.routeService.regulatorVltCreate(this.regulatorId) }
          : undefined;
      })
    );

    this.regulatorsService.detail(this.regulatorId).subscribe({
      next: (response: any) => this.applyRegulator(response?.data?.regulator),
      error: () => this.router.navigate(['/error/404'])
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  goBack(): void {
    this.routeService.regulatorsIndex();
  }

  private applyRegulator(regulator: Regulator | null | undefined): void {
    if (!regulator) {
      return;
    }

    this.contextItems = [{ label: 'Regulator', code: regulator.code, value: regulator.name }];
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'VLT' },
    ]);
  }
}
